import { useEffect, useState } from 'react';
import {
  DiscoveryApi,
  FetchApi,
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { usePollingTick } from '../../hooks/usePollingTick';
import { ENVIRONMENTS } from '../deployments/shared';

/**
 * The ArgoCD instance name as configured in app-config.yaml under
 * argocd.appLocatorMethods[0].instances[0].name
 */
const ARGOCD_INSTANCE = 'main';

/** Deployment health is operational data; refresh once a minute. */
export const ARGO_ENV_STATUSES_POLL_INTERVAL_MS = 60 * 1000;

/**
 * All environments we query — one selector request per environment. The
 * canonical list lives in components/deployments/shared.
 */
export const ARGO_ENVIRONMENTS = ENVIRONMENTS;

export interface ArgoEnvStatus {
  sync: string;
  health: string;
}

/**
 * Shape of the Roadie ArgoCD backend selector response:
 * GET <base>/argoInstance/<instance>/applications/selector/<selector>
 */
export interface ArgoSelectorResponse {
  items?: Array<{
    metadata?: { name?: string };
    status?: {
      sync?: { status?: string };
      health?: { status?: string };
    };
  }> | null;
}

/**
 * Flatten a list of selector responses (one per environment) into a single
 * Map keyed by ArgoCD Application name. Application names are unique across
 * environments (they are prefixed with the environment by the gitops
 * ApplicationSets), so a flat map covers every app.
 *
 * Failed environment queries are passed in as `undefined` and simply
 * contribute nothing — the UI then shows the tag only for those envs.
 */
export function buildStatusMap(
  responses: Array<ArgoSelectorResponse | undefined>,
): Map<string, ArgoEnvStatus> {
  const map = new Map<string, ArgoEnvStatus>();
  for (const response of responses) {
    for (const item of response?.items ?? []) {
      const name = item?.metadata?.name;
      if (!name) continue;
      map.set(name, {
        sync: item?.status?.sync?.status ?? 'Unknown',
        health: item?.status?.health?.status ?? 'Unknown',
      });
    }
  }
  return map;
}

async function fetchAllEnvStatuses(
  discoveryApi: DiscoveryApi,
  fetchApi: FetchApi,
): Promise<Map<string, ArgoEnvStatus>> {
  const base = await discoveryApi.getBaseUrl('argocd');
  const responses = await Promise.all(
    ARGO_ENVIRONMENTS.map(
      async (env): Promise<ArgoSelectorResponse | undefined> => {
        try {
          const selector = encodeURIComponent(`environment=${env}`);
          const url = `${base}/argoInstance/${ARGOCD_INSTANCE}/applications/selector/${selector}`;
          const resp = await fetchApi.fetch(url);
          if (!resp.ok) return undefined;
          return (await resp.json()) as ArgoSelectorResponse;
        } catch {
          // Failure for one env → empty contribution for that env
          return undefined;
        }
      },
    ),
  );
  return buildStatusMap(responses);
}

/**
 * Module-level promise cache: the catalog index renders one status cell per
 * row × environment, but all of them must share the same one-shot batch of
 * 4 selector requests (one per environment) regardless of row count.
 */
let statusMapPromise: Promise<Map<string, ArgoEnvStatus>> | undefined;
let statusMapGeneration = 0;
const statusMapListeners = new Set<() => void>();

export function invalidateArgoEnvStatusesCache(): void {
  statusMapPromise = undefined;
  statusMapGeneration += 1;
  statusMapListeners.forEach(listener => listener());
}

/** Test-only escape hatch to clear the module-level cache. */
export function resetArgoEnvStatusesCache(): void {
  statusMapPromise = undefined;
  statusMapGeneration = 0;
}

/**
 * Returns the shared app-name → sync/health map, or `undefined` while it is
 * still loading. The underlying fetch happens at most once per page load.
 */
export function useArgoEnvStatuses(): Map<string, ArgoEnvStatus> | undefined {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [statuses, setStatuses] = useState<
    Map<string, ArgoEnvStatus> | undefined
  >(undefined);
  const pollTick = usePollingTick(ARGO_ENV_STATUSES_POLL_INTERVAL_MS);
  const [cacheGeneration, setCacheGeneration] = useState(statusMapGeneration);

  useEffect(() => {
    const listener = () => setCacheGeneration(statusMapGeneration);
    statusMapListeners.add(listener);
    return () => {
      statusMapListeners.delete(listener);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (pollTick > 0) {
      statusMapPromise = undefined;
    }

    if (!statusMapPromise) {
      statusMapPromise = fetchAllEnvStatuses(discoveryApi, fetchApi).catch(
        error => {
          // Don't poison the cache permanently on unexpected failure
          statusMapPromise = undefined;
          throw error;
        },
      );
    }

    statusMapPromise.then(
      map => {
        if (!cancelled) setStatuses(map);
      },
      () => {
        if (!cancelled) setStatuses(new Map());
      },
    );

    return () => {
      cancelled = true;
    };
  }, [discoveryApi, fetchApi, pollTick, cacheGeneration]);

  return statuses;
}
